import { usePopoverPosition } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { Popover, PopoverOrigin, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import React, {
  cloneElement,
  createElement,
  memo,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Icon } from '../icon';
import * as Styled from './styles';
import { PopoverActionProps as ActionProps, CustomPopoverProps } from './types';
import usePopover from './use-popover';
import { getPosition } from './utils';

const POPOVER_POSITION_OPTIONS = { thresholdPercentage: 50 } as const;

const CONTENT_BASE_SX = {
  width: 'auto',
  overflow: 'auto',
} as const;

function CustomPopoverInner({
  className,
  children,
  arrowPosition: arrow = 'top-right',
  showArrow,
  arrowSx,
  sx,
  element,
  state: _state,
  title,
  titleSx,
  icon,
  content,
  contentSx,
  actions,
  actionsSx,
  backdropClosable,
  openClassName = 'open',
  onClose,
  ...other
}: CustomPopoverProps) {
  const positionConfig = useMemo(() => getPosition(arrow), [arrow]);
  const defaultState = usePopover();
  const state = _state ?? defaultState;
  const resolvedContent = (children as ReactNode) ?? content;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { anchorOrigin: popoverAnchor, transformOrigin: popoverOrigin } =
    usePopoverPosition(anchorEl, POPOVER_POSITION_OPTIONS);

  const handleClose = useCallback(() => {
    if (backdropClosable === false || state.loading) return;
    state.onClose();
    onClose?.();
  }, [backdropClosable, state, onClose]);

  const renderAction = useCallback(
    ({ showSpinner, closer, ...props }: ActionProps, key: number) =>
      createElement(LoadingButton, {
        ...props,
        key,
        [showSpinner ? 'loading' : 'disabled']: state.loading,
        ...(props.disabled ? { disabled: props.disabled } : {}),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
          type ActionClickArg = Parameters<
            NonNullable<ActionProps['onClick']>
          >[0];
          props.onClick?.({ event, ...state } as unknown as ActionClickArg);
          if (closer) state.onClose();
        },
      }),
    [state]
  );

  const stopPropagationForTab = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Tab') {
        event.stopPropagation();
      }
    },
    []
  );

  const triggerElement = useMemo(() => {
    if (!element) return null;
    return cloneElement(element, {
      ...element.props,
      className: classnames(element.props.className, {
        [openClassName]: state.open,
      }),
      onClick: (e: MouseEvent<HTMLElement>) => {
        state.onOpen(e.currentTarget as HTMLElement);
      },
    });
  }, [element, openClassName, state]);

  const paperSx = useMemo(
    () => mergeSx(CONTENT_BASE_SX, positionConfig.style, sx),
    [positionConfig.style, sx]
  );

  const slotProps = useMemo(
    () => ({
      paper: { sx: paperSx },
    }),
    [paperSx]
  );

  const anchorOrigin = (popoverAnchor ??
    positionConfig.anchorOrigin) as PopoverOrigin;
  const transformOrigin = (popoverOrigin ??
    positionConfig.transformOrigin) as PopoverOrigin;

  const renderedActions = useMemo(
    () => actions?.map(renderAction),
    [actions, renderAction]
  );

  return (
    <>
      {triggerElement}

      <Popover
        className={classnames('CustomPopover', className)}
        open={!!state.open}
        anchorEl={other.anchorEl ?? state.open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={slotProps}
        closeAfterTransition
        onClose={handleClose}
        onKeyDown={stopPropagationForTab}
        {...other}
      >
        {showArrow && <Styled.Arrow sx={arrowSx} arrow={arrow} />}
        <Styled.Content className="CustomPopoverContent">
          {title != null && title !== '' && (
            <Styled.Title className="CustomPopoverTitle">
              {icon != null && <Icon name={icon} size={20} color="grey.500" />}
              <Typography
                sx={titleSx}
                variant="body4"
                fontWeight={600}
                color="black"
              >
                {title}
              </Typography>
            </Styled.Title>
          )}

          <Styled.Main sx={contentSx} className="CustomPopoverMainContent">
            {resolvedContent}
          </Styled.Main>

          {actions != null && (
            <Styled.Actions sx={actionsSx}>{renderedActions}</Styled.Actions>
          )}
        </Styled.Content>
      </Popover>
    </>
  );
}

const CustomPopover = memo(CustomPopoverInner);
CustomPopover.displayName = 'CustomPopover';

export default CustomPopover;
